import React, { useMemo } from 'react';
import uuid from 'uuid/v4';
import { cloneDeep } from 'lodash';
import { setHeightOfRows } from '../../../util/CrosstabUtils';

import InfoBox, { InfoBoxSpacer } from '../EnhancedGridFeature/InfoBox';
import GridItemName from '../EnhancedGridFeature/GridItemName';
import EditGridItem from '../EnhancedGridFeature/EditGridItem';
import EditExpression from '../EnhancedGridFeature/EditExpression';
import blueEllipsisIcon from '../../../images/blue-menu-icon.svg';
import GlobalDelete from '../EnhancedGridFeature/GlobalDelete';
import Label from '../EnhancedGridFeature/Label';

import AutocodeLabelsGenerator, {
  ACStandardLabels,
} from '../../autocode/AutocodeLabelsGenerator';

const { toLabelString } = AutocodeLabelsGenerator(ACStandardLabels, null);

function PrivateEyeViewRowHeaders({
  getGridRows,
  toggleCheckVal,
  updateGridData,
  handleCrosstabEdit,
  restoreGridTitle,
  singleDeleteOperation,
  updateCrosstabGridTitleHeader,
  crosstabFootNotes,
  updatedTitleIndex,
  updateGridTitle,
  updatedTitleGridType,
  resetSelectedIndex,
  editExpression,
  partsToShow,
}) {
  const newRows = cloneDeep(getGridRows);

  const getHeaderLabel = (header, fromMenu = false) => {
    let title = '';
    if (header.isRowLabelUpdated && !fromMenu) {
      title = header.rowHeader;
    } else if (header.autocodenode) {
      title = toLabelString(header.autocodenode, partsToShow, fromMenu);
    } else {
      title = `${header.tooltipVal ? header.tooltipVal : header.rowHeader}`;
    }
    /** fix added for PLAT-15661
     * autocode node not forming correctly for NOT operator when it's added from smartsearch
     */
    if (title.includes('error')) {
      title = header.rowHeader;
    }
    return title;
  };

  const buildInfoBox = (row, showingFullNames, rowIndex) => {
    const ibp = [];
    if (!showingFullNames || row.isRowLabelUpdated) {
      const txt = getHeaderLabel(row, true);
      ibp.push(
        <GridItemName
          header="Original Name"
          body={txt}
          ShowRestoreButton={row.isRowLabelUpdated}
          restoreGridTitle={restoreGridTitle}
          index={rowIndex}
          gridType="RowsDataGrid"
        />
      );
      ibp.push(
        <InfoBoxSpacer
          Height="1em"
          boxClassName="toolPalette-spacer-row ibox-spacer-hline"
        />
      );
    }
    ibp.push(
      <EditGridItem
        updateGridTitle={updateGridTitle}
        index={rowIndex}
        gridType="RowsDataGrid"
      />
    );
    ibp.push(
      <InfoBoxSpacer
        Height="0.1em"
        boxClassName="toolPalette-spacer-row ibox-spacer-hline"
      />
    );
    ibp.push(
      <EditExpression
        handleEditExpression={handleCrosstabEdit}
        index={rowIndex}
        gridType="RowsDataGrid"
      />
    );
    ibp.push(
      <InfoBoxSpacer
        Height="0.1em"
        boxClassName="toolPalette-spacer-row ibox-spacer-hline"
      />
    );
    ibp.push(
      <GlobalDelete
        label="Clear Row"
        deleteType="single"
        gridType="RowsDataGrid"
        type="row"
        infoBox
        index={rowIndex}
        singleDeleteOperation={singleDeleteOperation}
      />
    );
    return ibp;
  };

  return useMemo(
    () =>
      newRows.map((row, rIndex) => (
        <div
          key={uuid()}
          className="c-table--row row-header-fixed"
          style={{
            height: setHeightOfRows(partsToShow, row, rIndex, toggleCheckVal),
          }}
        >
          <div
            className={
              toggleCheckVal.row
                ? 'padding-row-title left-header-content left-header-content-ellipsis'
                : 'left-header-content left-header-content-ellipsis'
            }
          >
            <button
              type="button"
              title={`${getHeaderLabel(row)} ${
                row.isRowLabelUpdated ? String.fromCharCode(185) : ''
              }`}
            >
              <Label
                editEnabled={
                  updatedTitleIndex === rIndex &&
                  updatedTitleGridType === 'RowsDataGrid'
                }
                label={getHeaderLabel(row)}
                toggleLabel={toggleCheckVal.row}
                gridType="RowsDataGrid"
                updateCrosstabGridTitleHeader={updateCrosstabGridTitleHeader}
                updateGridData={updateGridData}
                resetSelectedIndex={resetSelectedIndex}
                index={rIndex}
                cols={50}
                minRows={1}
                maxRows={9}
                isLabelUpdated={row.isRowLabelUpdated}
                style={{
                  left: '0px',
                  backgroundColor: '#EDF0F3',
                  color: 'black',
                  'min-height': '35px',
                }}
                showFootNoteIcon={row.footnotes ? row.footnotes : []}
                crosstabFootNotes={crosstabFootNotes}
              />
            </button>
            {rIndex !== 0 && (
              <div className="c-table--row-infobox-ellipsis">
                <InfoBox
                  Panels={buildInfoBox(row, toggleCheckVal.row, rIndex)}
                  Positions={['right']}
                  InfoBoxButtonInner={
                    <img
                      className="ibox-ellipsis ibox-ellipsis-top-padding"
                      src={blueEllipsisIcon}
                      alt="..."
                    />
                  }
                  Align="start"
                  containerClassName={
                    getHeaderLabel(row).length < 390
                      ? 'ibox-popover-container'
                      : !toggleCheckVal.row || row.isRowLabelUpdated
                      ? 'ibox-popover-container-longText'
                      : 'ibox-popover-container'
                  }
                />
              </div>
            )}
          </div>
        </div>
      )),
    [
      getGridRows,
      toggleCheckVal.row,
      crosstabFootNotes,
      updatedTitleIndex,
      updatedTitleGridType,
      editExpression,
    ]
  );
}

export default React.memo(PrivateEyeViewRowHeaders);
